import { useMemo, useState } from "react";
import { revealLinkedFile } from "@/components/shapes/file-frame/linked-file-actions";
import type { FrameChromeMenuItem } from "@/components/shapes/shared/frame-chrome-config";
import { frameDownloadVaries } from "@/components/shapes/shared/frame-download";
import { useFrameDetail } from "@/components/shapes/shared/use-frame-detail";
import type { WidgetSize } from "@/components/shapes/shared/widget-face";
import {
  widgetSizesToPick,
  widgetSizingFor,
} from "@/components/shapes/shared/widget-sizing";
import {
  openDesktopPinInFocus,
  pinToDesktop,
  unpinFromDesktop,
} from "@/desktop/pin/desktop-pin-actions";
import type { FrameData } from "@/lib/frame-data";
import { previewSource } from "@/lib/frame-preview-source";
import { linkHere } from "@/lib/local-file-link";
import { enterExplicitFocus } from "@/lib/focus-presentation";
import {
  archiveFrames,
  copyFrame,
  deleteFrames,
  downloadFrame,
  unarchiveFrame,
} from "@/lib/frame-menu-actions";
import type { FrameMenuGroups, FrameMenuSurface } from "@/lib/frame-menu-model";
import { setFramePinned } from "@/lib/frame-pinning";
import { openTaskDetails } from "@/lib/right-panel-navigation";
import {
  frameMenuContent,
  frameMenuFacts,
  singleFrameMenuGroups,
  type FrameMenuFacts,
} from "./card-menu-items";
import {
  WallCardMenu,
  type FrameMenuTarget,
  type WallCardMenuAction,
  type WallCardMenuTarget,
} from "./wall-card-menu";
import { useWallFrame } from "./wall-frame-source";

interface WallCardMenuHostProps {
  /** The card whose menu is open; `null` closes it. */
  target: WallCardMenuTarget | null;
  /**
   * `card`: a wall card or Pinned tile (its pill has Open and Pin);
   * `opened`: the opened item's ⋯ and right-click (its pill has ⛶);
   * `pin`: a desktop widget's ⋯ (plan 12), its size row and Open first.
   */
  surface: Exclude<FrameMenuSurface, "selection">;
  onClose: () => void;
  /**
   * The frame's rendered root, for Copy's selection and a chart's PNG.
   * Absent on a wall card, which shows only the card.
   */
  frameRoot?: () => HTMLElement | null;
  /** An opened item's type extras (`WallCardMenu`'s `extras`). */
  extras?: readonly FrameChromeMenuItem[];
  /** The text a right-click landed on: Copy selection leads the menu. */
  selection?: string;
  /**
   * The frame an opened surface shows, for one the wall's stores do not
   * have yet (a fresh docked chat).
   */
  shownFrame?: FrameData;
  /** A desktop widget's size and its change, for its size row. */
  widgetSize?: WidgetSize | null;
  onWidgetSize?: (size: WidgetSize) => void;
}

const EMPTY_GROUPS: FrameMenuGroups = {
  size: [],
  restore: [],
  go: [],
  content: [],
  place: [],
  end: [],
};

interface Shown {
  target: WallCardMenuTarget;
  facts: FrameMenuFacts;
}

/**
 * One frame's menu wired to the shared actions (Part 04 decision 12): a
 * wall card's and Pinned tile's ⋯ and right-click, an Archived card's
 * (Unarchive first), the opened item's ⋯ and right-click (led by the
 * right-clicked selection and its type extras) and a desktop widget's ⋯
 * (plan 12; its Open and Unpin go to the owner overlay as Tauri events).
 * Copy and Download load a card-only frame's content when clicked; only a
 * frame whose formats depend on its content loads it as the menu opens, its
 * Download shown once known.
 */
export function WallCardMenuHost({
  target,
  surface,
  onClose,
  frameRoot,
  extras,
  selection = "",
  shownFrame,
  widgetSize,
  onWidgetSize,
}: WallCardMenuHostProps) {
  // What the stores say is read once per opening; the menu keeps its last
  // card while it fades out.
  const [shown, setShown] = useState<Shown | null>(null);
  if (target && target !== shown?.target) {
    setShown({ target, facts: frameMenuFacts(target.frameId) });
  }
  const stored = useWallFrame(shown?.target.frameId);
  const frame =
    stored ??
    (shownFrame?.frameId === shown?.target.frameId ? shownFrame : undefined);
  // The frame as the open menu saw it: a closed menu does not re-read a
  // streaming frame's content.
  const [menuFrame, setMenuFrame] = useState<FrameData | null>(null);
  const open = target !== null;
  if (open && frame && frame !== menuFrame) setMenuFrame(frame);
  const rendered = frameRoot !== undefined;
  const root = () => frameRoot?.() ?? null;
  // A foreign card offers nothing that reads its content: no detail.
  const detail = useFrameDetail(
    menuFrame?.frameId ?? "",
    open &&
      menuFrame !== null &&
      menuFrame.contentState === "card" &&
      !shown?.facts.foreign &&
      frameDownloadVaries(menuFrame, { rendered }),
  );
  // Decided again at every opening (`shown`): a chat's Copy reads its
  // rendered thread, which changes without the frame changing.
  const content = useMemo(
    () =>
      menuFrame && shown
        ? frameMenuContent(menuFrame, { root: frameRoot?.() ?? null, rendered })
        : null,
    [frameRoot, menuFrame, rendered, shown],
  );
  const groups = useMemo(
    () =>
      // A target missing from the stores never shows the last card's menu.
      menuFrame && content && menuFrame.frameId === shown?.target.frameId
        ? singleFrameMenuGroups(menuFrame, {
            surface,
            facts: shown.facts,
            content: {
              copy: content.copy,
              // A failed detail has nothing to download (its toast has shown).
              download: content.download && !detail.failed,
              showFile: content.showFile,
            },
          })
        : null,
    [content, detail.failed, menuFrame, shown, surface],
  );
  const menuTarget = useMemo<FrameMenuTarget | null>(
    () =>
      target ? { frameIds: [target.frameId], anchor: target.anchor } : null,
    [target],
  );

  const handleAction = (_: readonly string[], action: WallCardMenuAction) => {
    if (!menuFrame) return;
    const frameId = menuFrame.frameId;
    switch (action) {
      case "unarchive":
        void unarchiveFrame(frameId, menuFrame);
        return;
      case "open":
        // Only a desktop widget offers Open: it opens in Focus.
        openDesktopPinInFocus(frameId);
        return;
      case "focus":
        enterExplicitFocus([frameId]);
        return;
      case "openTask":
        if (menuFrame.taskTicket) void openTaskDetails(menuFrame.taskTicket.id);
        return;
      case "copy":
        // The latest content, not the snapshot the menu opened with.
        void copyFrame(frame ?? menuFrame, { root: root() });
        return;
      case "showFile": {
        const link = linkHere(previewSource(menuFrame));
        if (link) void revealLinkedFile({ link, frameId });
        return;
      }
      case "pin":
      case "unpin":
        setFramePinned(frameId, action === "pin");
        return;
      case "pinToDesktop":
        pinToDesktop(frameId);
        return;
      case "unpinFromDesktop":
        unpinFromDesktop(frameId);
        return;
      case "archive":
        void archiveFrames([frameId], [menuFrame]);
        return;
      case "delete":
        void deleteFrames([frameId]);
        return;
    }
  };

  return (
    <WallCardMenu
      target={groups ? menuTarget : null}
      groups={groups ?? EMPTY_GROUPS}
      extras={extras}
      onCopySelection={
        selection && menuFrame
          ? () => void copyFrame(menuFrame, { root: root(), selection })
          : undefined
      }
      downloadOptions={content?.options ?? null}
      onDownload={(option) => {
        if (menuFrame) void downloadFrame(menuFrame, { option, root: root() });
      }}
      onClose={onClose}
      onAction={handleAction}
      widgetSize={widgetSize}
      widgetSizes={
        menuFrame ? widgetSizesToPick(widgetSizingFor(menuFrame.frameType)) : []
      }
      onWidgetSize={onWidgetSize}
    />
  );
}
